// app/javascript/controllers/tabs_faqs_controller.js
import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["tab", "content"];
  static values = {
    activeTab: String,
    activeClass: String,
    inactiveClass: String,
    animationDuration: { type: Number, default: 300 },
    animationType: { type: String, default: "fade" }, // "fade", "slide", "scale"
  };

  connect() {
    // Définir les classes par défaut si elles ne sont pas spécifiées
    if (!this.hasActiveClassValue) {
      this.activeClassValue = "bg-cta text-white";
    }
    if (!this.hasInactiveClassValue) {
      this.inactiveClassValue = "bg-gray-4 hover:bg-gray-2 hover:text-white";
    }

    // Initialiser les styles d'animation pour tous les contenus
    this.initializeAnimationStyles();

    // Activer le premier onglet par défaut ou celui spécifié
    const defaultTab =
      this.activeTabValue || this.tabTargets[0]?.dataset.tabName || "tous";
    this.showTab(defaultTab, false); // false pour éviter l'animation au premier chargement
  }

  // Initialiser les styles d'animation
  initializeAnimationStyles() {
    this.contentTargets.forEach((content) => {
      content.style.transition = `opacity ${this.animationDurationValue}ms ease-in-out, transform ${this.animationDurationValue}ms ease-in-out`;
      content.classList.add("transition-all");

      // Styles initiaux selon le type d'animation - seulement pour les contenus cachés
      if (content.classList.contains("hidden")) {
        switch (this.animationTypeValue) {
          case "fade":
            content.style.opacity = "0";
            break;
          case "slide":
            content.style.opacity = "0";
            content.style.transform = "translateX(20px)";
            break;
          case "scale":
            content.style.opacity = "0";
            content.style.transform = "scale(0.95)";
            break;
        }
      } else {
        // Pour le contenu actif initial, s'assurer qu'il est visible
        content.style.opacity = "1";
        content.style.transform = "none";
      }
    });

    // Animation pour les onglets eux-mêmes
    this.tabTargets.forEach((tab) => {
      tab.style.transition = `all ${this.animationDurationValue}ms ease-in-out`;
      tab.classList.add("transition-all");
    });
  }

  // Action appelée lors du clic sur un onglet
  selectTab(event) {
    event.preventDefault();
    const tabName = event.currentTarget.dataset.tabName;
    this.showTab(tabName, true); // true pour activer l'animation
  }

  // Méthode pour afficher un onglet spécifique
  showTab(tabName, animate = true) {
    const activeContent = this.contentTargets.find(
      (content) => content.dataset.tabContent === tabName
    );
    const previousContent = this.contentTargets.find(
      (content) => !content.classList.contains("hidden")
    );

    if (!activeContent) return;

    // Si c'est le même onglet, ne rien faire
    if (previousContent === activeContent) return;

    // Animation des onglets
    this.animateTabChange(tabName);

    if (animate && previousContent) {
      // Animation de sortie du contenu précédent
      this.animateContentOut(previousContent).then(() => {
        // Cacher le contenu précédent
        previousContent.classList.add("hidden");
        previousContent.setAttribute("aria-hidden", "true");

        // Afficher et animer le nouveau contenu
        this.showAndAnimateContent(activeContent);
      });
    } else {
      // Cacher tout le contenu sans animation
      this.contentTargets.forEach((content) => {
        content.classList.add("hidden");
        content.setAttribute("aria-hidden", "true");
        this.resetContentAnimation(content);
      });

      // Afficher le nouveau contenu
      this.showAndAnimateContent(activeContent, true);
    }

    // Mettre à jour la valeur active
    this.activeTabValue = tabName;

    // Dispatch un événement personnalisé
    this.dispatch("tabChanged", {
      detail: {
        activeTab: tabName,
        previousTab: previousContent?.dataset.tabContent,
      },
    });
  }

  // Animer le changement d'onglet
  animateTabChange(tabName) {
    // Désactiver tous les onglets avec animation
    this.tabTargets.forEach((tab) => {
      tab.classList.remove(...this.activeClassValue.split(" "));
      tab.classList.add(...this.inactiveClassValue.split(" "));
      tab.setAttribute("aria-selected", "false");

      // Effet de "pulse" pour l'onglet désactivé
      tab.style.transform = "scale(1)";
    });

    // Activer l'onglet sélectionné avec animation
    const activeTab = this.tabTargets.find(
      (tab) => tab.dataset.tabName === tabName
    );
    if (activeTab) {
      activeTab.classList.remove(...this.inactiveClassValue.split(" "));
      activeTab.classList.add(...this.activeClassValue.split(" "));
      activeTab.setAttribute("aria-selected", "true");

      // Effet de "pulse" pour l'onglet actif
      activeTab.style.transform = "scale(1.05)";
      setTimeout(() => {
        activeTab.style.transform = "scale(1)";
      }, this.animationDurationValue / 2);
    }
  }

  // Animation de sortie du contenu
  animateContentOut(content) {
    return new Promise((resolve) => {
      switch (this.animationTypeValue) {
        case "fade":
          content.style.opacity = "0";
          break;
        case "slide":
          content.style.opacity = "0";
          content.style.transform = "translateX(-20px)";
          break;
        case "scale":
          content.style.opacity = "0";
          content.style.transform = "scale(0.95)";
          break;
      }

      setTimeout(resolve, this.animationDurationValue / 2);
    });
  }

  // Afficher et animer le nouveau contenu
  showAndAnimateContent(content, immediate = false) {
    content.classList.remove("hidden");
    content.setAttribute("aria-hidden", "false");

    if (immediate) {
      // Affichage immédiat sans animation
      content.style.opacity = "1";
      content.style.transform = "none";
      return;
    }

    // Animation d'entrée
    requestAnimationFrame(() => {
      switch (this.animationTypeValue) {
        case "fade":
          content.style.opacity = "1";
          break;
        case "slide":
          content.style.opacity = "1";
          content.style.transform = "translateX(0)";
          break;
        case "scale":
          content.style.opacity = "1";
          content.style.transform = "scale(1)";
          break;
      }
    });
  }

  // Réinitialiser l'animation du contenu
  resetContentAnimation(content) {
    switch (this.animationTypeValue) {
      case "fade":
        content.style.opacity = "0";
        break;
      case "slide":
        content.style.opacity = "0";
        content.style.transform = "translateX(20px)";
        break;
      case "scale":
        content.style.opacity = "0";
        content.style.transform = "scale(0.95)";
        break;
    }
  }

  // Méthode utilitaire pour obtenir l'onglet actif
  get activeTab() {
    return this.activeTabValue;
  }

  // Méthode pour naviguer programmatiquement
  goToTab(tabName, animate = true) {
    this.showTab(tabName, animate);
  }

  // Support du clavier (flèches gauche/droite)
  handleKeydown(event) {
    if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) {
      return;
    }

    event.preventDefault();

    const currentIndex = this.tabTargets.findIndex(
      (tab) => tab.dataset.tabName === this.activeTabValue
    );

    let newIndex;
    switch (event.key) {
      case "ArrowLeft":
        newIndex =
          currentIndex > 0 ? currentIndex - 1 : this.tabTargets.length - 1;
        break;
      case "ArrowRight":
        newIndex =
          currentIndex < this.tabTargets.length - 1 ? currentIndex + 1 : 0;
        break;
      case "Home":
        newIndex = 0;
        break;
      case "End":
        newIndex = this.tabTargets.length - 1;
        break;
    }

    const newTab = this.tabTargets[newIndex];
    if (newTab) {
      this.showTab(newTab.dataset.tabName, true);
      newTab.focus();
    }
  }
}
